import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Empty,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Upload,
    message,
} from 'antd';
import type { UploadProps } from 'antd';
import { CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';
import {
    useApi,
    type ContractDocumentAssetPayload,
    type ContractDocumentTemplatePayload,
} from '../contexts/ApiContext';
import {
    buildDocumentVariableGroups,
    CONTRACT_DOCUMENT_VARIABLE_GROUP_SPECS,
} from '../domain/documentVariables';
import RichTextEditor from './RichTextEditor';

const MAX_ASSET_BYTES = 5 * 1024 * 1024;

const IFRAME_PREVIEW_STYLE: CSSProperties = {
    width: '100%',
    height: '70vh',
    border: '1px solid #d9d9d9',
};
const LOADING_STYLE: CSSProperties = { textAlign: 'center', padding: 48 };

/**
 * Replace legacy Handlebars `{{#each Objects}}...{{/each}}` blocks with the
 * single `{{ObjectsTable}}` placeholder so the WYSIWYG editor can present a
 * clean editable representation. The runtime render service still supports
 * both forms; this transformation is only for the editing experience.
 */
const normalizeObjectsTablePlaceholder = (html: string): string => {
    if (!html) return html;
    const pattern = /\{\{#each\s+Objects\s*\}\}[\s\S]*?\{\{\/each\}\}/g;
    if (!pattern.test(html)) return html;
    return html.replace(pattern, '{{ObjectsTable}}');
};

const fileToBase64 = (file: File): Promise<{ base64: string; mimeType: string }> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('read error'));
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const commaIndex = result.indexOf(',');
            const base64 = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;
            resolve({ base64, mimeType: file.type || 'application/octet-stream' });
        };
        reader.readAsDataURL(file);
    });

interface ContractDocumentEditorProps {
    open: boolean;
    onClose: () => void;
}

const ContractDocumentEditor = ({ open, onClose }: ContractDocumentEditorProps) => {
    const { t } = useTranslation();
    const { contractDocument } = useApi();

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [template, setTemplate] = useState<ContractDocumentTemplatePayload | null>(null);
    const [templatesList, setTemplatesList] = useState<ContractDocumentTemplatePayload[]>([]);
    const [assets, setAssets] = useState<ContractDocumentAssetPayload[]>([]);
    const [htmlContent, setHtmlContent] = useState('');
    const [cssContent, setCssContent] = useState('');
    const [templateName, setTemplateName] = useState('');
    // Track whether the user actually edited each field. The WYSIWYG editor
    // (TipTap) does not preserve unknown wrappers like `<section>`, custom
    // `<div class="contract-...">`, etc. that the template CSS hooks into,
    // so a parse + serialize round-trip silently strips structure. When the
    // user makes no changes we must persist the ORIGINAL loaded HTML/CSS
    // verbatim instead of whatever the editor regurgitates.
    const originalHtmlRef = useRef('');
    const originalCssRef = useRef('');
    const originalNameRef = useRef('');
    const [htmlDirty, setHtmlDirty] = useState(false);
    const [cssDirty, setCssDirty] = useState(false);
    const [nameDirty, setNameDirty] = useState(false);
    const [previewNr, setPreviewNr] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const variableGroups = useMemo(
        () =>
            buildDocumentVariableGroups(CONTRACT_DOCUMENT_VARIABLE_GROUP_SPECS, t, label => label),
        [t],
    );

    const templateOptions = useMemo(
        () =>
            templatesList.map(t => ({
                value: t.id,
                label: t.name || `Template #${t.id}`,
            })),
        [templatesList],
    );

    const loadTemplatesList = useCallback(async () => {
        const response = await contractDocument.listTemplates();
        if (response.success && response.data) {
            setTemplatesList(response.data.templates);
        }
    }, [contractDocument]);

    const refreshTemplate = useCallback(
        async (templateId?: number) => {
            setLoading(true);
            try {
                const response = templateId
                    ? await contractDocument.getTemplate(templateId)
                    : await contractDocument.getActiveTemplate();
                if (response.success && response.data) {
                    const loaded = response.data.template;
                    setTemplate(loaded);
                    setAssets(response.data.assets);
                    const initialHtml = normalizeObjectsTablePlaceholder(loaded.htmlContent ?? '');
                    const initialCss = loaded.cssContent ?? '';
                    const initialName = loaded.name ?? '';
                    setHtmlContent(initialHtml);
                    setCssContent(initialCss);
                    setTemplateName(initialName);
                    originalHtmlRef.current = initialHtml;
                    originalCssRef.current = initialCss;
                    originalNameRef.current = initialName;
                    setHtmlDirty(false);
                    setCssDirty(false);
                    setNameDirty(false);
                } else {
                    message.error(
                        response.error ?? t('configuration.contractDocument.errors.loadFailed'),
                    );
                    setTemplate(null);
                    setAssets([]);
                }
            } finally {
                setLoading(false);
            }
        },
        [contractDocument, t],
    );

    useEffect(() => {
        if (!open) return;
        loadTemplatesList();
        refreshTemplate();
    }, [open, loadTemplatesList, refreshTemplate]);

    useEffect(
        () => () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        },
        [previewUrl],
    );

    /**
     * Build the saveDraft payload, sending only fields the user actually
     * edited. Untouched fields fall back to the original loaded values so we
     * never persist a lossy editor round-trip of the HTML structure.
     */
    const buildDraftPayload = () => ({
        name: nameDirty ? templateName : originalNameRef.current,
        htmlContent: htmlDirty ? htmlContent : originalHtmlRef.current,
        cssContent: cssDirty ? cssContent : originalCssRef.current,
    });

    const applySavedTemplate = (saved: ContractDocumentTemplatePayload) => {
        setTemplate(saved);
        const savedHtml = normalizeObjectsTablePlaceholder(saved.htmlContent ?? '');
        const savedCss = saved.cssContent ?? '';
        const savedName = saved.name ?? '';
        setHtmlContent(savedHtml);
        setCssContent(savedCss);
        setTemplateName(savedName);
        originalHtmlRef.current = savedHtml;
        originalCssRef.current = savedCss;
        originalNameRef.current = savedName;
        setHtmlDirty(false);
        setCssDirty(false);
        setNameDirty(false);
    };

    const handleSaveDraft = async () => {
        if (!template) return;
        setBusy(true);
        try {
            const response = await contractDocument.saveDraft(template.id, buildDraftPayload());
            if (response.success && response.data) {
                applySavedTemplate(response.data.template);
                message.success(t('configuration.contractDocument.messages.draftSaved'));
            } else {
                message.error(
                    response.error ?? t('configuration.contractDocument.errors.saveFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const handleSaveAsNew = async () => {
        if (!template) return;
        const newName = window.prompt(
            t('configuration.contractDocument.saveAsNewName') || 'Enter new template name:',
        );
        if (!newName?.trim()) return;

        setBusy(true);
        try {
            const response = await contractDocument.duplicate(template.id, {
                ...buildDraftPayload(),
                name: newName,
            });
            if (response.success && response.data) {
                applySavedTemplate(response.data.template);
                setAssets(response.data.assets);
                message.success(t('configuration.contractDocument.messages.draftSaved'));
                loadTemplatesList();
            } else {
                message.error(
                    response.error ?? t('configuration.contractDocument.errors.saveFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const handleNameChange = (next: string) => {
        setTemplateName(next);
        setNameDirty(true);
    };

    const handleHtmlChange = useCallback((next: string) => {
        setHtmlContent(next);
        // Only flag as dirty when the editor produces something genuinely
        // different from the originally loaded HTML. This protects against
        // TipTap firing a synthetic `update` event during initialization
        // (e.g. via `setEditable`) which would otherwise persist the lossy
        // re-serialized HTML even though the user typed nothing.
        if (next !== originalHtmlRef.current) {
            setHtmlDirty(true);
        }
    }, []);

    const handleCssChange = (next: string) => {
        setCssContent(next);
        if (next !== originalCssRef.current) {
            setCssDirty(true);
        }
    };

    const handlePublish = async () => {
        if (!template) return;
        setBusy(true);
        try {
            const saved = await contractDocument.saveDraft(template.id, buildDraftPayload());
            if (!saved.success) {
                message.error(saved.error ?? t('configuration.contractDocument.errors.saveFailed'));
                return;
            }
            const response = await contractDocument.publish(template.id);
            if (response.success && response.data) {
                applySavedTemplate(response.data.template);
                message.success(t('configuration.contractDocument.messages.published'));
            } else {
                message.error(
                    response.error ?? t('configuration.contractDocument.errors.publishFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const handleRestoreDefault = async () => {
        if (!template) return;
        Modal.confirm({
            title: t('configuration.contractDocument.confirmRestoreTitle'),
            content: t('configuration.contractDocument.confirmRestoreContent'),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            onOk: async () => {
                setBusy(true);
                try {
                    const factoryResponse = await contractDocument.getFactoryDefault();
                    if (!factoryResponse.success || !factoryResponse.data) {
                        message.error(
                            factoryResponse.error ??
                                t('configuration.contractDocument.errors.factoryDefaultFailed'),
                        );
                        return;
                    }
                    const factory = factoryResponse.data;
                    const response = await contractDocument.restoreDefault(template.id, {
                        defaultHtmlContent: factory.htmlContent,
                        defaultCssContent: factory.cssContent,
                    });
                    if (response.success && response.data) {
                        applySavedTemplate(response.data.template);
                        message.success(t('configuration.contractDocument.messages.restored'));
                    } else {
                        message.error(
                            response.error ??
                                t('configuration.contractDocument.errors.restoreFailed'),
                        );
                    }
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const handlePreview = async () => {
        const nr = parseInt(previewNr.trim(), 10);
        if (isNaN(nr) || nr <= 0) {
            message.warning(t('configuration.contractDocument.errors.contractNumberRequired'));
            return;
        }
        setBusy(true);
        setPreviewError(null);
        try {
            const response = await contractDocument.previewByNr(nr);
            if (response.success && response.data) {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl(URL.createObjectURL(response.data));
            } else {
                setPreviewError(
                    response.error ?? t('configuration.contractDocument.errors.previewFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const uploadAsset = async (
        file: File,
        assetType: 'logo' | 'page_background',
        pageNumber: number | null,
    ) => {
        if (!template) return false;
        if (file.size > MAX_ASSET_BYTES) {
            message.error(t('configuration.contractDocument.errors.assetTooLarge'));
            return false;
        }
        setBusy(true);
        try {
            const { base64, mimeType } = await fileToBase64(file);
            const response = await contractDocument.uploadAsset(template.id, {
                assetType,
                pageNumber,
                mimeType: file.type || mimeType,
                contentBase64: base64,
            });
            if (response.success) {
                message.success(t('configuration.contractDocument.messages.assetSaved'));
                const refreshed = await contractDocument.listAssets(template.id);
                if (refreshed.success && refreshed.data) {
                    setAssets(refreshed.data.assets);
                }
            } else {
                message.error(
                    response.error ?? t('configuration.contractDocument.errors.assetFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
        return false; // prevent default upload
    };

    const handleDeleteAsset = async (assetId: number) => {
        if (!template) return;
        setBusy(true);
        try {
            const response = await contractDocument.deleteAsset(template.id, assetId);
            if (response.success) {
                setAssets(prev => prev.filter(asset => asset.id !== assetId));
                message.success(t('configuration.contractDocument.messages.assetDeleted'));
            } else {
                message.error(
                    response.error ?? t('configuration.contractDocument.errors.assetDeleteFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const findAsset = (
        assetType: 'logo' | 'page_background',
        pageNumber: number | null,
    ): ContractDocumentAssetPayload | undefined =>
        assets.find(
            asset =>
                asset.assetType === assetType &&
                (pageNumber === null ? asset.pageNumber === null : asset.pageNumber === pageNumber),
        );

    const renderAssetSlot = (
        labelKey: string,
        assetType: 'logo' | 'page_background',
        pageNumber: number | null,
    ) => {
        const existing = findAsset(assetType, pageNumber);
        const existingInfo = existing
            ? `${existing.mimeType} · ${Math.round(existing.sizeBytes / 1024)} KB`
            : '';
        const uploadProps: UploadProps = {
            multiple: false,
            showUploadList: false,
            accept: 'image/*',
            beforeUpload: file => {
                uploadAsset(file, assetType, pageNumber);
                return false;
            },
        };
        return (
            <Card size="small" style={{ marginBottom: 8 }}>
                <Space
                    direction="horizontal"
                    style={{ width: '100%', justifyContent: 'space-between' }}
                    wrap
                >
                    <span>
                        <strong>{t(labelKey)}</strong>
                        {existing ? (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                                {existingInfo}
                            </Tag>
                        ) : (
                            <Tag style={{ marginLeft: 8 }}>
                                {t('configuration.contractDocument.assetMissing')}
                            </Tag>
                        )}
                    </span>
                    <Space>
                        <Upload {...uploadProps}>
                            <Button icon={<CloudUploadOutlined />} size="small">
                                {t('configuration.contractDocument.uploadAsset')}
                            </Button>
                        </Upload>
                        {existing ? (
                            <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                                onClick={() => handleDeleteAsset(existing.id)}
                            >
                                {t('common.delete')}
                            </Button>
                        ) : null}
                    </Space>
                </Space>
            </Card>
        );
    };

    const previewBlock = useMemo(() => {
        if (previewError) {
            return <Alert type="error" message={previewError} />;
        }
        if (!previewUrl) {
            return (
                <Empty
                    description={t('configuration.contractDocument.previewEmpty')}
                    style={{ padding: 24 }}
                />
            );
        }
        return (
            <iframe
                title="contract-document-preview"
                src={previewUrl}
                style={IFRAME_PREVIEW_STYLE}
            />
        );
    }, [previewError, previewUrl, t]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width="calc(92vw - 180px)"
            style={{ top: 24 }}
            styles={{ wrapper: { paddingLeft: '180px' } }}
            title={
                <Space>
                    {t('configuration.contractDocument.editorTitle')}
                    <Select
                        value={template?.id}
                        style={{ width: 200, marginLeft: 16 }}
                        placeholder={t('configuration.contractDocument.selectTemplate')}
                        options={templateOptions}
                        onChange={val => refreshTemplate(val)}
                        disabled={loading || busy}
                    />
                </Space>
            }
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('common.close')}
                </Button>,
                <Button key="restore" onClick={handleRestoreDefault} disabled={!template || busy}>
                    {t('configuration.contractDocument.restoreDefault')}
                </Button>,
                <Button
                    key="draft"
                    onClick={handleSaveDraft}
                    disabled={!template || busy}
                    loading={busy}
                >
                    {t('configuration.contractDocument.saveDraft')}
                </Button>,
                <Button
                    key="saveAsNew"
                    onClick={handleSaveAsNew}
                    disabled={!template || busy}
                    loading={busy}
                >
                    {t('configuration.contractDocument.saveAsNew')}
                </Button>,
                <Button
                    key="publish"
                    type="primary"
                    onClick={handlePublish}
                    disabled={!template || busy}
                    loading={busy}
                >
                    {t('configuration.contractDocument.publish')}
                </Button>,
            ]}
        >
            {loading && (
                <div style={LOADING_STYLE}>
                    <Spin />
                </div>
            )}
            {!loading && !template && (
                <Empty description={t('configuration.contractDocument.noTemplate')} />
            )}
            {!loading && template && (
                <Row gutter={16}>
                    <Col span={12}>
                        <Form layout="vertical">
                            <Form.Item label={t('configuration.contractDocument.templateName')}>
                                <Input
                                    value={templateName}
                                    onChange={event => handleNameChange(event.target.value)}
                                    maxLength={100}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('configuration.contractDocument.contentLabel')}
                                help={t('configuration.contractDocument.contentHelp')}
                            >
                                <RichTextEditor
                                    value={htmlContent}
                                    onChange={handleHtmlChange}
                                    variableGroups={variableGroups}
                                    disabled={busy}
                                />
                            </Form.Item>
                            <Collapse
                                size="small"
                                items={[
                                    {
                                        key: 'advanced',
                                        label: t('configuration.contractDocument.advancedTitle'),
                                        children: (
                                            <>
                                                <Alert
                                                    type="warning"
                                                    showIcon
                                                    message={t(
                                                        'configuration.contractDocument.advancedHint',
                                                    )}
                                                    style={{ marginBottom: 8 }}
                                                />
                                                <Form.Item
                                                    label={t(
                                                        'configuration.contractDocument.htmlContent',
                                                    )}
                                                >
                                                    <Input.TextArea
                                                        value={htmlContent}
                                                        onChange={event =>
                                                            handleHtmlChange(event.target.value)
                                                        }
                                                        autoSize={{ minRows: 8, maxRows: 18 }}
                                                        spellCheck={false}
                                                        style={{
                                                            fontFamily: 'monospace',
                                                            fontSize: 12,
                                                        }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={t(
                                                        'configuration.contractDocument.cssContent',
                                                    )}
                                                >
                                                    <Input.TextArea
                                                        value={cssContent}
                                                        onChange={event =>
                                                            handleCssChange(event.target.value)
                                                        }
                                                        autoSize={{ minRows: 6, maxRows: 14 }}
                                                        spellCheck={false}
                                                        style={{
                                                            fontFamily: 'monospace',
                                                            fontSize: 12,
                                                        }}
                                                    />
                                                </Form.Item>
                                            </>
                                        ),
                                    },
                                ]}
                            />
                        </Form>

                        <Card
                            size="small"
                            title={t('configuration.contractDocument.assetsTitle')}
                            style={{ marginTop: 8 }}
                        >
                            {renderAssetSlot('configuration.contractDocument.logo', 'logo', null)}
                            {renderAssetSlot(
                                'configuration.contractDocument.pageBackground1',
                                'page_background',
                                1,
                            )}
                            {renderAssetSlot(
                                'configuration.contractDocument.pageBackground2',
                                'page_background',
                                2,
                            )}
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card size="small" style={{ marginBottom: 8 }}>
                            <Space.Compact style={{ width: '100%' }}>
                                <Input
                                    value={previewNr}
                                    onChange={event => setPreviewNr(event.target.value)}
                                    placeholder={t(
                                        'configuration.contractDocument.previewContractNumber',
                                    )}
                                />
                                <Button
                                    type="primary"
                                    onClick={handlePreview}
                                    loading={busy}
                                    disabled={!template}
                                >
                                    {t('configuration.contractDocument.previewButton')}
                                </Button>
                            </Space.Compact>
                        </Card>
                        {previewBlock}
                    </Col>
                </Row>
            )}
        </Modal>
    );
};

export default ContractDocumentEditor;
